import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const LAND_TYPES = ['Farmland', 'Orchard', 'Plantation', 'Greenhouse', 'Other'];
const AREA_UNITS = ['Acre', 'Hectare', 'Bigha', 'Cent'];

const STATUS_BADGE = { pending:'badge--warning', active:'badge--success', rejected:'badge--danger' };
const STATUS_LABEL = { pending:'Pending', active:'Live', rejected:'Rejected' };

// Rent and Lease are both recurring (shown "/mo"); Sale is a one-time price
const RECURRING_TYPES = ['rent', 'lease'];
const isRecurring = (t) => RECURRING_TYPES.includes(t);

const EMPTY_ADD_FORM = {
  listingType: 'lease', title: '', price: '', address: '', area: '',
  areaUnit: 'Acre', landType: 'Farmland', frontage: '', soilType: '',
  landUse: '', description: '', photos: '', ownerName: '', ownerMobile: '',
};

export default function LandPage() {
  const [listings,  setListings]  = useState([]);
  const [loading,    setLoading]  = useState(true);
  const [stats,      setStats]    = useState(null);
  const [total,      setTotal]    = useState(0);
  const [page,       setPage]     = useState(1);
  const [filters,    setFilters]  = useState({ status:'', listingType:'', landType:'', search:'' });
  const [editListing,setEditListing] = useState(null);
  const [newStatus,  setNewStatus]   = useState('');
  const [adminNote,  setAdminNote]   = useState('');
  const [saving,     setSaving]      = useState(false);
  const [exporting,  setExporting]   = useState(false);
  const [viewListing,setViewListing] = useState(null);
  const [showAdd,    setShowAdd]     = useState(false);
  const [addForm,    setAddForm]     = useState(EMPTY_ADD_FORM);
  const [addSaving,  setAddSaving]   = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef();

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadListings(); }, [filters, page]);

  const setF = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };

  const loadStats = async () => {
    try { const { data } = await api.get('/admin/land/stats'); setStats(data.stats); }
    catch {}
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get('/admin/land?' + params);
      setListings(data.listings || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load listings'); }
    finally { setLoading(false); }
  };

  const saveStatus = async () => {
    if (!editListing) return;
    setSaving(true);
    try {
      await api.put('/admin/land/' + editListing._id + '/status', { status: newStatus, adminNote });
      setListings(p => p.map(l => l._id === editListing._id ? { ...l, status: newStatus } : l));
      toast.success('Status updated to ' + STATUS_LABEL[newStatus]);
      setEditListing(null); loadStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const deleteListing = async (item) => {
    if (!window.confirm('Delete "' + item.title + '"? Cannot be undone.')) return;
    try {
      await api.delete('/admin/land/' + item._id);
      setListings(p => p.filter(l => l._id !== item._id));
      setTotal(p => p - 1);
      toast.success('Listing deleted'); loadStats();
    } catch { toast.error('Error'); }
  };

  const setAddField = (k, v) => setAddForm(p => ({ ...p, [k]: v }));
  const openAdd = () => { setAddForm(EMPTY_ADD_FORM); setUploadedPhotos([]); setShowAdd(true); };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const { data } = await api.post('/upload/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedPhotos(p => [...p, ...data.files.map(f => f.url)]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Image upload failed');
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const removeUploadedPhoto = (url) => setUploadedPhotos(p => p.filter(u => u !== url));

  const submitAdd = async () => {
    if (!addForm.price)          { toast.error('Price is required');   return; }
    if (!addForm.address.trim()) { toast.error('Address is required'); return; }

    setAddSaving(true);
    try {
      const pastedUrls = addForm.photos.split('\n').map(s => s.trim()).filter(Boolean);
      const photos = [...uploadedPhotos, ...pastedUrls];
      const { data } = await api.post('/admin/land', { ...addForm, photos });
      setListings(p => [data.listing, ...p]);
      setTotal(p => p + 1);
      toast.success('Listing added and published');
      setShowAdd(false);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to add listing'); }
    finally { setAddSaving(false); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get('/admin/land/export?' + params);
      if (!data.data?.length) { toast.error('No data to export'); return; }
      const headers = Object.keys(data.data[0]);
      const csv = [
        headers.join(','),
        ...data.data.map(r => headers.map(h => '"' + String(r[h] ?? '').replace(/"/g, '""') + '"').join(','))
      ].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      a.download = 'land-' + Date.now() + '.csv';
      a.click();
      toast.success('Exported ' + data.data.length + ' listings');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get('/admin/land/export?' + params);
      if (!data.data?.length) { toast.error('No data'); return; }
      const rows = data.data;
      const headerRow = Object.keys(rows[0]).map(h => '<th>' + h + '</th>').join('');
      const dataRows  = rows.map(r => '<tr>' + Object.values(r).map(v => '<td>' + String(v ?? '') + '</td>').join('') + '</tr>').join('');
      const html = `<html><head><title>Land Report</title>
        <style>body{font-family:sans-serif;font-size:11px}table{width:100%;border-collapse:collapse}
        th{background:#166534;color:white;padding:5px 8px;text-align:left}
        td{padding:4px 8px;border-bottom:1px solid #eee}h2{color:#166534}
        @media print{button{display:none}}</style></head>
        <body><h2>ZevaZeva Land Report</h2>
        <p>Generated: ${new Date().toLocaleString('en-IN')} | Total: ${rows.length}</p>
        <table><tr>${headerRow}</tr>${dataRows}</table>
        <script>window.print()<\/script></body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
    } catch { toast.error('PDF failed'); }
    finally { setExporting(false); }
  };

  return (
    <AdminLayout title="Land Management">
      {/* Stats */}
      {stats && (
        <div className="stat-grid" style={{ gridTemplateColumns:'repeat(5,1fr)', marginBottom:20 }}>
          {[
            { label:'Total',    value:stats.total,    icon:'🏞️', filter:'' },
            { label:'Pending',  value:stats.pending,  icon:'⏳', filter:'pending' },
            { label:'Live',     value:stats.active,   icon:'🟢', filter:'active' },
            { label:'Rejected', value:stats.rejected, icon:'❌', filter:'rejected' },
            { label:'This Week',value:stats.newThisWeek, icon:'📅', filter:'' },
          ].map(s => (
            <div key={s.label} className="stat-card" style={{ cursor:'pointer' }}
              onClick={() => setF('status', s.filter)}>
              <div className="stat-card__icon">{s.icon}</div>
              <div className="stat-card__label">{s.label}</div>
              <div className="stat-card__value">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ marginBottom:16 }}>
        <div className="card__body--sm">
          {/* Status pills */}
          <div className="pill-bar">
            {['', 'pending', 'active', 'rejected'].map(s => (
              <button key={s} className={'pill' + (filters.status === s ? ' pill--active' : '')}
                onClick={() => setF('status', s)}>
                {s ? STATUS_LABEL[s] : 'All Status'}
              </button>
            ))}
          </div>

          {/* Listing type pills */}
          <div className="pill-bar">
            <button className={'pill' + (!filters.listingType ? ' pill--active' : '')} onClick={() => setF('listingType', '')}>All Types</button>
            <button className={'pill' + (filters.listingType === 'rent' ? ' pill--active' : '')} onClick={() => setF('listingType', 'rent')}>Rent</button>
            <button className={'pill' + (filters.listingType === 'lease' ? ' pill--active' : '')} onClick={() => setF('listingType', 'lease')}>Lease</button>
            <button className={'pill' + (filters.listingType === 'sale' ? ' pill--active' : '')} onClick={() => setF('listingType', 'sale')}>Sale</button>
          </div>

          {/* Land type pills */}
          <div className="pill-bar">
            <button className={'pill' + (!filters.landType ? ' pill--active' : '')} onClick={() => setF('landType', '')}>All Land Types</button>
            {LAND_TYPES.map(t => (
              <button key={t} className={'pill' + (filters.landType === t ? ' pill--active' : '')}
                onClick={() => setF('landType', t)}>{t}</button>
            ))}
          </div>

          {/* Search + export */}
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <input className="input" placeholder="🔍 Search title or address..." value={filters.search}
              onChange={e => setF('search', e.target.value)} style={{ flex:1 }} />
            <button className="btn btn--primary btn--sm" onClick={openAdd}>+ Add Land</button>
            <button className="btn btn--outline btn--sm" onClick={exportCSV} disabled={exporting}>📊 CSV</button>
            <button className="btn btn--outline btn--sm" onClick={exportPDF} disabled={exporting}>📄 PDF</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Property</th><th>Owner</th><th>Type</th><th>Status</th>
                <th>Price</th><th>Area</th><th>Added</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {listings.length === 0 ? (
                  <tr><td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state__icon">🏞️</div>
                      <div className="empty-state__title">No listings found</div>
                    </div>
                  </td></tr>
                ) : listings.map(item => (
                  <tr key={item._id}>
                    <td>
                      <div style={{ fontWeight:600, cursor:'pointer', color:'#166534' }} onClick={() => setViewListing(item)}>
                        {item.title}
                      </div>
                      <div style={{ fontSize:11, color:'#6C757D' }}>{item.landType || '—'}</div>
                    </td>
                    <td>
                      <div style={{ fontSize:13, fontWeight:500 }}>{item.ownerName || item.userId?.name || '—'}</div>
                      <div style={{ fontSize:11, color:'#2563EB' }}>{item.ownerMobile || item.userId?.mobile}</div>
                    </td>
                    <td style={{ textTransform:'capitalize' }}>{item.listingType}</td>
                    <td>
                      <span className={'badge ' + (STATUS_BADGE[item.status] || 'badge--gray')}>
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </td>
                    <td>{item.price ? '₹' + item.price.toLocaleString('en-IN') : '—'}{isRecurring(item.listingType) ? '/mo' : ''}</td>
                    <td>{item.area ? item.area + ' ' + item.areaUnit : '—'}</td>
                    <td style={{ fontSize:12 }}>{new Date(item.createdAt).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn--outline btn--sm" onClick={() => setViewListing(item)}>👁</button>
                        <button className="btn btn--primary btn--sm"
                          onClick={() => { setEditListing(item); setNewStatus(item.status); setAdminNote(''); }}>
                          🔄
                        </button>
                        <button className="btn btn--danger btn--sm" onClick={() => deleteListing(item)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="pagination">
          <button className="btn btn--outline btn--sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/20)} · {total} total</span>
          <button className="btn btn--outline btn--sm" disabled={page>=Math.ceil(total/20)} onClick={() => setPage(p=>p+1)}>Next →</button>
        </div>
      )}

      {/* View details modal */}
      {viewListing && (
        <div className="modal-overlay" onClick={() => setViewListing(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">🏞️ {viewListing.title}</div>
              <button className="modal__close" onClick={() => setViewListing(null)}>×</button>
            </div>
            <div className="modal__body">
              <div style={{ fontSize:12, color:'#6C757D', marginBottom:12 }}>
                Owner: {viewListing.ownerName || viewListing.userId?.name || '—'} · +91 {viewListing.ownerMobile || viewListing.userId?.mobile || '—'}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14, fontSize:13 }}>
                <div><strong>Address:</strong> {viewListing.address}</div>
                <div><strong>Price:</strong> ₹{viewListing.price?.toLocaleString('en-IN')}{isRecurring(viewListing.listingType) ? '/mo' : ''}</div>
                <div><strong>Area:</strong> {viewListing.area ? `${viewListing.area} ${viewListing.areaUnit}` : '—'}</div>
                <div><strong>Land Type:</strong> {viewListing.landType || '—'}</div>
                <div><strong>Frontage:</strong> {viewListing.frontage || '—'}</div>
                <div><strong>Soil Type:</strong> {viewListing.soilType || '—'}</div>
                <div><strong>Land Use:</strong> {viewListing.landUse || '—'}</div>
                <div><strong>Views:</strong> {viewListing.viewCount || 0}</div>
              </div>

              {viewListing.description && (
                <div style={{ marginBottom:14 }}>
                  <strong style={{ fontSize:13 }}>Description</strong>
                  <div style={{ fontSize:13, color:'#495057', marginTop:4 }}>{viewListing.description}</div>
                </div>
              )}

              {viewListing.amenities?.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <strong style={{ fontSize:13 }}>Amenities</strong>
                  <div style={{ fontSize:12, color:'#495057', marginTop:4 }}>{viewListing.amenities.join(', ')}</div>
                </div>
              )}

              {viewListing.photos?.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <strong style={{ fontSize:13 }}>Photos</strong>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                    {viewListing.photos.map((p, i) => (
                      <a key={i} href={p} target="_blank" rel="noreferrer">
                        <img src={p} alt="" style={{ width:64, height:64, objectFit:'cover', borderRadius:6, border:'1px solid #dee2e6' }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom:4 }}>
                <strong style={{ fontSize:13 }}>Verification Documents</strong>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                  {viewListing.ownerDocUrl
                    ? <a className="btn btn--outline btn--sm" href={viewListing.ownerDocUrl} target="_blank" rel="noreferrer">📄 Owner Doc ({viewListing.ownerDocType || 'ID'})</a>
                    : <span style={{ fontSize:12, color:'#adb5bd' }}>No owner document uploaded</span>}
                  {viewListing.selfieUrl &&
                    <a className="btn btn--outline btn--sm" href={viewListing.selfieUrl} target="_blank" rel="noreferrer">🤳 Selfie</a>}
                  {viewListing.extraDocUrl &&
                    <a className="btn btn--outline btn--sm" href={viewListing.extraDocUrl} target="_blank" rel="noreferrer">📎 Extra Document</a>}
                </div>
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setViewListing(null)}>Close</button>
              <button className="btn btn--primary" onClick={() => {
                setEditListing(viewListing); setNewStatus(viewListing.status); setAdminNote(''); setViewListing(null);
              }}>🔄 Review Status</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit status modal */}
      {editListing && (
        <div className="modal-overlay" onClick={() => setEditListing(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">🔄 Change Status — {editListing.title}</div>
              <button className="modal__close" onClick={() => setEditListing(null)}>×</button>
            </div>
            <div className="modal__body">
              <div style={{ fontSize:12, color:'#6C757D', marginBottom:12 }}>
                Owner: {editListing.ownerName || editListing.userId?.name || '—'} · +91 {editListing.ownerMobile || editListing.userId?.mobile || '—'}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:14 }}>
                {[
                  { v:'pending',  label:'⏳ Pending',  desc:'Awaiting verification' },
                  { v:'active',   label:'🟢 Approve',  desc:'Make it live' },
                  { v:'rejected', label:'❌ Reject',   desc:'Send back to owner' },
                ].map(s => (
                  <button key={s.v} onClick={() => setNewStatus(s.v)}
                    style={{ padding:'10px 12px', borderRadius:8,
                      border:'2px solid ' + (newStatus===s.v ? '#166534' : '#dee2e6'),
                      background: newStatus===s.v ? '#DCFCE7' : 'white',
                      textAlign:'left', cursor:'pointer' }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{s.label}</div>
                    <div style={{ fontSize:11, color:'#6C757D', marginTop:2 }}>{s.desc}</div>
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label className="label">Admin Note (optional)</label>
                <textarea className="input" rows={2} value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                  placeholder="Reason for rejection or note for the owner..." />
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setEditListing(null)}>Cancel</button>
              <button className="btn btn--primary" onClick={saveStatus} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save Status'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add land modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">➕ Add Land Listing</div>
              <button className="modal__close" onClick={() => setShowAdd(false)}>×</button>
            </div>
            <div className="modal__body">
              <div style={{ fontSize:12, color:'#6C757D', marginBottom:14 }}>
                Published live immediately — no owner verification needed for admin-added listings.
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:14 }}>
                {[{ v:'rent', l:'Rent' }, { v:'lease', l:'Lease' }, { v:'sale', l:'Sale' }].map(o => (
                  <button key={o.v} onClick={() => setAddField('listingType', o.v)}
                    style={{ padding:'10px 12px', borderRadius:8,
                      border:'2px solid ' + (addForm.listingType===o.v ? '#166534' : '#dee2e6'),
                      background: addForm.listingType===o.v ? '#DCFCE7' : 'white',
                      fontWeight:600, cursor:'pointer' }}>
                    {o.l}
                  </button>
                ))}
              </div>

              <div className="form-group">
                <label className="label">Title (optional)</label>
                <input className="input" value={addForm.title} onChange={e => setAddField('title', e.target.value)}
                  placeholder="Auto-generated from area + land type if left blank" />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="label">Owner Name</label>
                  <input className="input" value={addForm.ownerName} onChange={e => setAddField('ownerName', e.target.value)}
                    placeholder="e.g. Yallappa" />
                </div>
                <div className="form-group">
                  <label className="label">Owner Mobile</label>
                  <input className="input" value={addForm.ownerMobile} onChange={e => setAddField('ownerMobile', e.target.value)}
                    placeholder="10-digit number" />
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="label">Price (₹) *</label>
                  <input className="input" type="number" value={addForm.price} onChange={e => setAddField('price', e.target.value)}
                    placeholder={isRecurring(addForm.listingType) ? 'Per month' : 'Total'} />
                </div>
                <div className="form-group">
                  <label className="label">Land Type</label>
                  <select className="input" value={addForm.landType} onChange={e => setAddField('landType', e.target.value)}>
                    {LAND_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="label">Address *</label>
                <input className="input" value={addForm.address} onChange={e => setAddField('address', e.target.value)}
                  placeholder="Village, taluk, district" />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="label">Area</label>
                  <input className="input" type="number" value={addForm.area} onChange={e => setAddField('area', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="label">Area Unit</label>
                  <select className="input" value={addForm.areaUnit} onChange={e => setAddField('areaUnit', e.target.value)}>
                    {AREA_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="label">Frontage</label>
                  <input className="input" value={addForm.frontage} onChange={e => setAddField('frontage', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="label">Soil Type</label>
                  <input className="input" value={addForm.soilType} onChange={e => setAddField('soilType', e.target.value)} />
                </div>
              </div>

              <div className="form-group">
                <label className="label">Land Use</label>
                <input className="input" value={addForm.landUse} onChange={e => setAddField('landUse', e.target.value)} />
              </div>

              <div className="form-group">
                <label className="label">Description</label>
                <textarea className="input" rows={3} value={addForm.description} onChange={e => setAddField('description', e.target.value)} />
              </div>

              <div className="form-group">
                <label className="label">Photos</label>
                <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={handlePhotoUpload} />
                <button type="button" className="btn btn--outline btn--sm" disabled={uploadingPhotos}
                  onClick={() => photoInputRef.current?.click()}>
                  {uploadingPhotos ? 'Uploading...' : '📷 Upload Images'}
                </button>

                {uploadedPhotos.length > 0 && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:10 }}>
                    {uploadedPhotos.map(url => (
                      <div key={url} style={{ position:'relative' }}>
                        <img src={url} alt="" style={{ width:64, height:64, objectFit:'cover', borderRadius:6, border:'1px solid #dee2e6' }} />
                        <button type="button" onClick={() => removeUploadedPhoto(url)}
                          style={{ position:'absolute', top:-6, right:-6, width:18, height:18, borderRadius:'50%', border:'none', background:'#DC2626', color:'white', fontSize:11, lineHeight:1, cursor:'pointer' }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Or paste photo URLs (one per line, optional)</label>
                <textarea className="input" rows={3} value={addForm.photos} onChange={e => setAddField('photos', e.target.value)}
                  placeholder={'https://example.com/photo1.jpg\nhttps://example.com/photo2.jpg'} />
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={submitAdd} disabled={addSaving}>
                {addSaving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Publish Listing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
